// Remove the standalone proxy's `__gshost` cache-key param from the browser address bar
// (#544, epic #543).
//
// The Laneful standalone proxy appends `?__gshost=<canonical customer host>` when it
// forwards a request to us, as a CDN cache-key workaround (the CDN keys on host+path+query
// but not on forwarded headers, and every standalone domain shares one proxy target).
//
// Server-side the param is already contained: `serveCustomDomainBlog` strips it before
// route matching, and `renderBlogForHost` only ever receives a pathname — so no emitted
// canonical / OG / RSS / sitemap URL can carry it. What that does NOT cover is the address
// bar, because TanStack Start's request middleware cannot hand a modified `Request` to the
// router (`RequestServerNextFn` takes only `{ context }`), so a request that falls through
// to the app keeps its raw URL. In practice only an UNTRUSTED `__gshost` gets there — a
// trusted one always resolves to a custom host and is served by `serveCustomDomainBlog` —
// but the param should never be visible to a user either way. See D-544-7.
//
// Deliberately self-contained rather than importing `stripProxyHostParam` from
// `functions/src/blog/hosts.ts`: that module reads `process.env.APP_HOSTS` at import time,
// which would throw in the browser bundle. `proxyHostAddressBar.test.ts` asserts this
// literal still matches the server-side `GSHOST_PARAM`, so the two can't drift.
export const GSHOST_ADDRESS_BAR_PARAM = "__gshost";

/**
 * Rewrite `history` in place so `__gshost` disappears from the address bar, preserving
 * every other query param, the path and the hash. A no-op when the param is absent, when
 * there is no `window` (SSR), or when the History API is unavailable.
 *
 * `replaceState` — not `pushState` — so the cleanup never adds a back-button entry.
 */
export function stripProxyHostParamFromAddressBar(): void {
  if (typeof window === "undefined") return;
  const { history, location } = window;
  if (!history?.replaceState) return;

  const url = new URL(location.href);
  if (!url.searchParams.has(GSHOST_ADDRESS_BAR_PARAM)) return;

  url.searchParams.delete(GSHOST_ADDRESS_BAR_PARAM);
  // Clearing `search` when nothing is left avoids leaving a bare trailing "?" behind.
  if (url.searchParams.size === 0) url.search = "";
  history.replaceState(history.state, "", `${url.pathname}${url.search}${url.hash}`);
}
